import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { InfoIcon } from '@/shared/ui/icons'
import styles from './InfoTip.module.css'

const GAP_PX = 4
const EDGE_PX = 12

type Position = { left: number; top: number }

type InfoTipProps = {
  /** The card's name, for the button's label. */
  name: string
  description: string
  className?: string | undefined
}

/**
 * A card's (i): explains the metric on hover or focus. The panel is portalled
 * to the body, since cards clip their overflow, and opens below the icon with
 * its right edge level with the icon's (above it when there's no room below).
 */
export const InfoTip = ({ name, description, className }: InfoTipProps) => {
  const id = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)

  useLayoutEffect(() => {
    const trigger = triggerRef.current?.getBoundingClientRect()
    const panel = panelRef.current
    if (!open || !trigger || !panel) return
    const left = Math.max(
      EDGE_PX,
      Math.min(
        trigger.right - panel.offsetWidth,
        window.innerWidth - panel.offsetWidth - EDGE_PX
      )
    )
    const opensAbove =
      trigger.bottom + GAP_PX + panel.offsetHeight > window.innerHeight
    setPosition({
      left,
      top: opensAbove
        ? trigger.top - panel.offsetHeight - GAP_PX
        : trigger.bottom + GAP_PX,
    })
  }, [open])

  const show = () => setOpen(true)
  const hide = useCallback(() => {
    setOpen(false)
    setPosition(null)
  }, [])

  // Escape closes from anywhere (the pointer may be over the panel, not the
  // button); scrolling or resizing would leave the fixed panel misplaced.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide, true)
    }
  }, [open, hide])

  // Leaving with the pointer must not dismiss a tip the keyboard is showing.
  const hideUnlessFocused = () => {
    if (document.activeElement !== triggerRef.current) hide()
  }

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className={`${styles.trigger} ${className ?? ''}`}
        aria-label={`About ${name}`}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hideUnlessFocused}
        onFocus={show}
        onBlur={hide}
      >
        <InfoIcon />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={id}
            role='tooltip'
            className={styles.panel}
            onMouseEnter={show}
            onMouseLeave={hideUnlessFocused}
            style={
              position
                ? { left: position.left, top: position.top }
                : { visibility: 'hidden' }
            }
          >
            {description}
          </div>,
          document.body
        )}
    </>
  )
}
